import React, {useState} from 'react';
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import type {ComplianceStandardResponse} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {CompanyStandardTable} from 'src/app/compliance-standards/CompanyStandardTable/CompanyStandardTable';
import {ComplianceStandardsBrowserShell} from 'src/app/compliance-standards/ComplianceStandardsBrowserShell';
import {complianceTreeRootId} from 'src/app/compliance-standards/complianceStandardTreeState';
import {InstanceComplianceStandardTable} from 'src/app/instance-compliance-standards/InstanceComplianceStandardTable/InstanceComplianceStandardTable';
import {SidebarLinkComplianceRegulatory} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import ImportCustomStandards from 'src/pages/Modules/Compliance/CustomComplianceStandard/CustomComplianceStandardList/components/ImportCustomStandards';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type ComplianceTab = 'company' | 'regulatory';

const ComplianceStandardListPage: React.FC = () => {
  useSetDocumentTitle('Compliance Standards');
  const navigate = useAppNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {setAlert} = useAlert();
  const {tab} = useParams<{tab?: string}>();

  const [showImport, setShowImport] = useState(false);
  const [regulatoryActiveOnly, setRegulatoryActiveOnly] = useState(true);
  const selectedNodeId = searchParams.get('node') ?? complianceTreeRootId;
  const scopeNodeId =
    selectedNodeId === complianceTreeRootId ? undefined : selectedNodeId;

  const handleNavigateToCompany = (entity: ComplianceStandardResponse) => {
    navigate(PathUtility.CustomComplianceStandardsView(entity.id!));
  };

  const handleNavigateToRegulatory = (entity: ComplianceStandardResponse) => {
    const id = entity.instanceComplianceStandards?.at(0)?.id;
    if (id !== undefined) {
      navigate(PathUtility.InstanceComplianceStandardsView(id));
    } else {
      navigate(PathUtility.InstanceComplianceStandardsCreate(entity.id!));
    }
  };

  const handleImportSuccess = () => {
    setShowImport(false);
    setAlert(
      'Your file has been successfully uploaded. You will receive an email after it has been imported with more information.',
      'success'
    );
  };

  const activeTab = (tab ?? 'company') as ComplianceTab;

  const {RenderedTabsComponent} = useUrlQueryTabV2<ComplianceTab>({
    view: activeTab,
    defaultView: 'company',
    tabs: [
      {
        view: 'company',
        label: 'Company',
        component: (
          <CompanyStandardTable
            identifier="compliance-company-tab"
            scopeNodeId={scopeNodeId}
            onViewRowClick={handleNavigateToCompany}
            onCreate={() =>
              navigate(PathUtility.CustomComplianceStandardsCreate())
            }
            onImport={() => setShowImport(true)}
          />
        ),
      },
      {
        view: 'regulatory',
        label: 'Regulatory',
        component: (
          <InstanceComplianceStandardTable
            identifier="compliance-regulatory-tab"
            scopeNodeId={scopeNodeId}
            activeOnly={regulatoryActiveOnly}
            onActiveOnlyChange={setRegulatoryActiveOnly}
            onViewRowClick={handleNavigateToRegulatory}
          />
        ),
      },
    ],
    onSelectView: view =>
      navigate({
        pathname: PathUtility.InstanceComplianceStandards(
          view as ComplianceTab
        ),
        search: location.search,
      }),
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Compliance}>
      <ComplianceStandardsBrowserShell
        variant={activeTab}
        selectedId={selectedNodeId}
        activeOnly={
          activeTab === 'regulatory' ? regulatoryActiveOnly : undefined
        }>
        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkComplianceRegulatory.icon}
            subtitle="Manage compliance standards">
            Compliance Standards
          </PageHeader.Title>
        </PageHeader>

        {RenderedTabsComponent}
      </ComplianceStandardsBrowserShell>

      {showImport && (
        <ImportCustomStandards
          show={showImport}
          onClose={() => setShowImport(false)}
          onImportSuccess={handleImportSuccess}
        />
      )}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.InstanceComplianceStandards(),
  element: <ComplianceStandardListPage />,
  module: ModuleEnum.Compliance,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.InstanceComplianceStandards(':tab'),
  element: <ComplianceStandardListPage />,
  module: ModuleEnum.Compliance,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default ComplianceStandardListPage;
